import { useState, useEffect, useCallback, useMemo } from 'react';

interface BreakpointConfig {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  '2xl': number;
}

interface ViewportInfo {
  width: number;
  height: number;
  breakpoint: keyof BreakpointConfig;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isTouch: boolean;
  orientation: 'portrait' | 'landscape';
  pixelRatio: number;
}

interface ResponsiveLayoutOptions {
  breakpoints?: Partial<BreakpointConfig>;
  debounceMs?: number;
}

const defaultBreakpoints: BreakpointConfig = {
  xs: 475,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
};

export const useResponsiveLayout = (options: ResponsiveLayoutOptions = {}) => {
  const { breakpoints = defaultBreakpoints, debounceMs = 150 } = options;
  
  const [viewportInfo, setViewportInfo] = useState<ViewportInfo>(() => {
    if (typeof window === 'undefined') {
      return {
        width: 1024,
        height: 768,
        breakpoint: 'lg',
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        isTouch: false,
        orientation: 'landscape',
        pixelRatio: 1
      };
    }

    return getViewportInfo();
  });

  function getViewportInfo(): ViewportInfo {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const pixelRatio = window.devicePixelRatio || 1;
    
    // Determine breakpoint
    let breakpoint: keyof BreakpointConfig = 'xs';
    if (width >= breakpoints['2xl']!) breakpoint = '2xl';
    else if (width >= breakpoints.xl!) breakpoint = 'xl';
    else if (width >= breakpoints.lg!) breakpoint = 'lg';
    else if (width >= breakpoints.md!) breakpoint = 'md';
    else if (width >= breakpoints.sm!) breakpoint = 'sm';

    // Device type detection
    const isMobile = width < breakpoints.md!;
    const isTablet = width >= breakpoints.md! && width < breakpoints.lg!;
    const isDesktop = width >= breakpoints.lg!;
    
    // Touch detection
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Orientation
    const orientation = width > height ? 'landscape' : 'portrait';

    return {
      width,
      height,
      breakpoint,
      isMobile,
      isTablet,
      isDesktop,
      isTouch,
      orientation,
      pixelRatio
    };
  }

  // Debounced resize handler
  const handleResize = useCallback(() => {
    let timeoutId: NodeJS.Timeout;
    
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setViewportInfo(getViewportInfo());
      }, debounceMs);
    };
  }, [debounceMs]);

  useEffect(() => {
    const debouncedHandler = handleResize();
    
    window.addEventListener('resize', debouncedHandler);
    window.addEventListener('orientationchange', debouncedHandler);
    
    return () => {
      window.removeEventListener('resize', debouncedHandler);
      window.removeEventListener('orientationchange', debouncedHandler);
    };
  }, [handleResize]);

  // Responsive utilities
  const isBreakpoint = useCallback((bp: keyof BreakpointConfig) => {
    return viewportInfo.breakpoint === bp;
  }, [viewportInfo.breakpoint]);

  const isBreakpointUp = useCallback((bp: keyof BreakpointConfig) => {
    const bpOrder: (keyof BreakpointConfig)[] = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'];
    const currentIndex = bpOrder.indexOf(viewportInfo.breakpoint);
    const targetIndex = bpOrder.indexOf(bp);
    return currentIndex >= targetIndex;
  }, [viewportInfo.breakpoint]);

  const isBreakpointDown = useCallback((bp: keyof BreakpointConfig) => {
    const bpOrder: (keyof BreakpointConfig)[] = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'];
    const currentIndex = bpOrder.indexOf(viewportInfo.breakpoint);
    const targetIndex = bpOrder.indexOf(bp);
    return currentIndex <= targetIndex;
  }, [viewportInfo.breakpoint]);

  // Responsive value selector
  const getResponsiveValue = useCallback(<T>(values: Partial<Record<keyof BreakpointConfig, T>>): T | undefined => {
    const bpOrder: (keyof BreakpointConfig)[] = ['2xl', 'xl', 'lg', 'md', 'sm', 'xs'];
    const currentBpIndex = bpOrder.indexOf(viewportInfo.breakpoint);
    
    // Find the first available value at or below current breakpoint
    for (let i = currentBpIndex; i < bpOrder.length; i++) {
      const bp = bpOrder[i];
      if (values[bp] !== undefined) {
        return values[bp];
      }
    }
    
    return undefined;
  }, [viewportInfo.breakpoint]);

  // Grid column calculator
  const getGridColumns = useCallback((config: Partial<Record<keyof BreakpointConfig, number>>) => {
    return getResponsiveValue(config) || 1;
  }, [getResponsiveValue]);

  // Spacing calculator
  const getSpacing = useCallback((config: Partial<Record<keyof BreakpointConfig, string>>) => {
    return getResponsiveValue(config) || '1rem';
  }, [getResponsiveValue]);

  // Performance recommendations based on device
  const performanceRecommendations = useMemo(() => {
    const recommendations = {
      reduceAnimations: false,
      limitParticles: false,
      useSimpleEffects: false,
      enableGPUAcceleration: true,
      preferCSS3D: false
    };

    if (viewportInfo.isMobile) {
      recommendations.reduceAnimations = true;
      recommendations.limitParticles = true;
      recommendations.useSimpleEffects = true;
    }

    if (viewportInfo.pixelRatio > 2) {
      recommendations.preferCSS3D = true;
    }

    // Low-end device detection
    const hardwareConcurrency = navigator.hardwareConcurrency || 2;
    const deviceMemory = (navigator as any).deviceMemory || 4;
    
    if (hardwareConcurrency <= 2 || deviceMemory <= 2) {
      recommendations.reduceAnimations = true;
      recommendations.limitParticles = true;
      recommendations.useSimpleEffects = true;
      recommendations.enableGPUAcceleration = false;
    }

    return recommendations;
  }, [viewportInfo]);

  // CSS classes generator
  const getResponsiveClasses = useCallback((classMap: Partial<Record<keyof BreakpointConfig, string>>) => {
    const classes: string[] = [];
    
    Object.entries(classMap).forEach(([bp, className]) => {
      if (className && isBreakpointUp(bp as keyof BreakpointConfig)) {
        classes.push(className);
      }
    });
    
    return classes.join(' ');
  }, [isBreakpointUp]);

  return {
    ...viewportInfo,
    isBreakpoint,
    isBreakpointUp,
    isBreakpointDown,
    getResponsiveValue,
    getGridColumns,
    getSpacing,
    getResponsiveClasses,
    performanceRecommendations,
    // Convenience methods
    isMobileOrTablet: viewportInfo.isMobile || viewportInfo.isTablet,
    isLandscape: viewportInfo.orientation === 'landscape',
    isPortrait: viewportInfo.orientation === 'portrait',
    isHighDPI: viewportInfo.pixelRatio > 1.5,
    isRetina: viewportInfo.pixelRatio >= 2
  };
};

// Hook for responsive font sizes
export const useResponsiveFontSize = (baseSize: number = 16) => {
  const { breakpoint } = useResponsiveLayout();
  
  const fontSizeMap = {
    xs: baseSize * 0.875,  // 14px if base is 16px
    sm: baseSize * 0.9375, // 15px if base is 16px
    md: baseSize,          // 16px
    lg: baseSize * 1.125,  // 18px if base is 16px
    xl: baseSize * 1.25,   // 20px if base is 16px
    '2xl': baseSize * 1.375 // 22px if base is 16px
  };
  
  return fontSizeMap[breakpoint];
};

// Hook for responsive spacing
export const useResponsiveSpacing = () => {
  const { breakpoint } = useResponsiveLayout();
  
  const spacingMap = {
    xs: { small: '0.5rem', medium: '1rem', large: '1.5rem' },
    sm: { small: '0.75rem', medium: '1.25rem', large: '2rem' },
    md: { small: '1rem', medium: '1.5rem', large: '2.5rem' },
    lg: { small: '1.25rem', medium: '2rem', large: '3rem' },
    xl: { small: '1.5rem', medium: '2.5rem', large: '4rem' },
    '2xl': { small: '2rem', medium: '3rem', large: '5rem' }
  };
  
  return spacingMap[breakpoint];
};